import React, { useState, useRef, useEffect } from "react";
import { AgGridReact } from "ag-grid-react";
import * as XLSX from "xlsx";
import { ModuleRegistry } from "ag-grid-community";
import { AllCommunityModule } from "ag-grid-community";
import LogoutButton from "./LogoutButton";
import api from "../api/api";
import "../App.css";
// or wherever you defined .modal-overlay and .modal

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";

// ✅ Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

const EmployeeDataGrid = () => {
  const [rowData, setRowData] = useState([]);
  const [columnDefs, setColumnDefs] = useState([]);
  const [fileName, setFileName] = useState("");
  const [dateValue, setDateValue] = useState("");
  const [shiftValue, setShiftValue] = useState("");
  const [shifts, setShifts] = useState([]);
  const [attendanceCount, setAttendanceCount] = useState(null);
  const [attendanceData, setAttendanceData] = useState([]);
  const [originalRowData, setOriginalRowData] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [empTypeOptions, setEmpTypeOptions] = useState([]);
  const [showSCModal, setShowSCModal] = useState(false);
  const [showEmpModal, setShowEmpModal] = useState(false);
  const [scData, setScData] = useState([]); // { SCName, Count }
  const [empTypeData, setEmpTypeData] = useState([]); // { EmpType, Count }
  const gridRef = useRef(null);
  const [showDeptModal, setShowDeptModal] = useState(false);
  const [deptData, setDeptData] = useState([]); // { EmpCode, EmpName, CurrentDept, SelectedDept }
  const [departments, setDepartments] = useState([]); // empty initially
  const [filters, setFilters] = useState({
    subMatched: false,
    subUnmatched: false,
    empMatched: false,
    empUnmatched: false,
  });
  const overlayStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  };

  const modalStyle = {
    backgroundColor: "#fff",
    padding: "20px",
    borderRadius: "8px",
    width: "60%",
    maxHeight: "80%",
    overflowY: "auto",
    position: "relative",
  };

  const thStyle = { border: "1px solid #ddd", padding: "8px", textAlign: "left" };
  const tdStyle = { border: "1px solid #ddd", padding: "8px" };
  // 🔹 Fetch shifts, vendors, and empTypes
  useEffect(() => {
    const fetchShiftsVendorsEmpTypesDepartments = async () => {
      try {
        const username = sessionStorage.getItem("username") || "admin";
        const response = await api.getShiftDetails(username);

        console.log("Shift API Response:", response); // 🔹 Debug

        // 1️⃣ Shifts
        if (response?.ShiftList?.Table) {
          setShifts(response.ShiftList.Table);
        } else {
          setShifts([]);
        }

        // 2️⃣ Vendors
        const vendorTable = response?.Table1 || response?.ShiftList?.Table1 || [];
        if (vendorTable.length) {
          setVendors(vendorTable.map(v => ({
            vid: v.vid,
            vname: v.vname,
          })));
        } else {
          setVendors([]);
        }

        // 3️⃣ EmpTypes
        const empTable = response?.Table2 || response?.ShiftList?.Table2 || [];
        if (empTable.length) {
          const empTypes = empTable
            .filter(item => item.Item_Desc2 && item.Item_Desc2.trim() !== "")
            .map(item => ({
              label: item.Item_Desc2,
              value: item.Item_Desc2,
              itemId: item.itemId
            }));
          setEmpTypeOptions(empTypes);
          console.log("✅ EmpType Options:", empTypes);
        } else {
          setEmpTypeOptions([]);
        }

        // 4️⃣ Departments (Table3)
        const deptTable = response?.Table3 || response?.ShiftList?.Table3 || [];
        if (deptTable.length) {
          const deptCodes = deptTable
            .map(item => item.code?.trim())
            .filter(code => code); // remove null/empty
          setDepartments(deptCodes);
          console.log("✅ Department Codes:", deptCodes);
        } else {
          setDepartments([]);
        }

      } catch (error) {
        console.error("❌ Failed to load API data:", error);
        setShifts([]);
        setVendors([]);
        setEmpTypeOptions([]);
        setDepartments([]);
      }
    };

    fetchShiftsVendorsEmpTypesDepartments();
  }, []);

  const handleUpdateSC = async () => {
    if (!dateValue || !shiftValue) return showToast("⚠️ Select Date & Shift first!", "orange");

    const shiftName = getShiftNameById(shiftValue);
    if (!shiftName) return showToast("⚠️ Invalid shift!", "orange");

    try {
      const res = await api.getScnameEmptypeStatus({ atDate: dateValue, shiftName, type: "SCName" });

      const processedSCData = (res?.Result?.Table || []).map(scItem => {
        const matchedVendor = (res?.Result?.Table2 || []).find(
          v => v.MatchedSCName === scItem.SCName
        );
        return {
          SCName: scItem.SCName,
          TOTCNT: scItem.TOTCNT || 0,
          CurrentVendor: matchedVendor?.vname || "-",
          CurrentVendorId: matchedVendor?.vid || null,
          SelectedVendor: matchedVendor?.vid || ""
        };
      });

      setScData(processedSCData);
      setShowSCModal(true);

    } catch (err) {
      console.error(err);
      showToast("❌ Failed to fetch SC data!", "red");
    }
  };

  const refreshAttendanceGrid = async () => {
    if (!dateValue || !shiftValue) return;
    const shiftName = getShiftNameById(shiftValue);
    if (!shiftName) return;

    try {
      const res = await api.getRefreshAttendance(dateValue, shiftName);
      if (res?.Result?.Table) {
        setRowData(res.Result.Table);
        setOriginalRowData(res.Result.Table);
        setTimeout(() => autoSizeAll(), 200);
        showToast("🔄 Attendance refreshed!", "green");
      }
    } catch (err) {
      console.error("❌ Refresh failed:", err);
      showToast("❌ Failed to refresh attendance!", "red");
    }
  };

  // EmpType
  const handleUpdateEmpType = async () => {
    if (!dateValue || !shiftValue) return showToast("⚠️ Select Date & Shift first!", "orange");

    const shiftName = getShiftNameById(shiftValue);
    if (!shiftName) return showToast("⚠️ Invalid shift!", "orange");

    try {
      const result = await api.getScnameEmptypeStatus({
        atDate: dateValue,
        shiftName,
        type: "EmpType"
      });

      const table1 = result?.Result?.Table1 || []; // counts
      const table3 = result?.Result?.Table3 || []; // master data

      const processedEmpTypeData = table3.map(master => {
        const countObj = table1.find(c => c.EmpType === master.EmpTypeValue || c.EmpType === master.EmpType) || {};
        return {
          EmpTypeLabel: master.EmpTypeValue || master.EmpType,
          TOTCNT: countObj.TOTCNT || 0,
          CurrentEmpType: master.EmpTypeValue || "",
          CurrentEmpTypeId: master.ItemId || "",
          SelectedEmpType: master.EmpTypeValue || master.EmpType
        };
      });

      setEmpTypeData(processedEmpTypeData);
      setShowEmpModal(true); // ✅ opens modal for user to select new EmpTypes
    } catch (err) {
      console.error(err);
      showToast("❌ Failed to fetch EmpType data!", "red");
    }
  };

  // Helper: get ShiftName from ShiftId
  const getShiftNameById = (id) => {
    const shift = shifts.find(s => s.ShiftId.toString() === id.toString());
    return shift ? shift.ShiftName : "";
  };
  const handleUpdateEmpTypeApi = async () => {
    if (!dateValue || !shiftValue) return alert("⚠️ Select Date & Shift!");

    const updates = empTypeData.map(item => ({
      EmpType: item.SelectedEmpType || item.CurrentEmpType,
      EmpCount: item.TOTCNT || 0,
      EmpTypeLabel: item.EmpTypeLabel,
      EmpTypeCode: item.SelectedEmpType || item.CurrentEmpType,
      ItemId: item.CurrentEmpTypeId
    }));

    if (!updates.length) return showToast("⚠️ No data to update!", "orange");

    try {
      const data = await api.updateEmpTypeAttendance(updates); // send array directly
      console.log("UpdateEmpTypeAttendance response:", data);
      showToast("✅ EmpType Attendance Updated!", "green");
      setShowEmpModal(false);
      await refreshAttendanceGrid();
      setEmpTypeData(prev =>
        prev.map(item => ({
          ...item,
          CurrentEmpType: item.SelectedEmpType || item.CurrentEmpType
        }))
      );
    } catch (err) {
      console.error(err);
      showToast("❌ Failed to update EmpType Attendance!", "red");
    }
  };

  const handleSCSelect = (oldSC, newSCId) => {
    if (!newSCId) return;
    const selectedVendor = vendors.find(v => v.vid.toString() === newSCId.toString());
    setScData(prev =>
      prev.map(item =>
        item.SCName === oldSC
          ? {
            ...item,
            SelectedVendor: selectedVendor.vname, // store name for display/API
            CurrentVendor: selectedVendor.vname,  // also update CurrentVendor
            CurrentVendorId: selectedVendor.vid   // store ID for binding
          }
          : item
      )
    );
  };
  const handleEmpTypeSelect = (index, newType, itemId) => {
    if (!newType) return;
    setEmpTypeData(prev =>
      prev.map((item, idx) =>
        idx === index
          ? {
            ...item,
            SelectedEmpType: newType,
            CurrentEmpType: newType,      // ✅ update CurrentEmpType immediately
            CurrentEmpTypeId: itemId      // ✅ update CurrentEmpTypeId immediately
          }
          : item
      )
    );
  };
  // 📂 Handle File Upload
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = async (e) => {
      const binaryStr = e.target.result;
      const workbook = XLSX.read(binaryStr, { type: "binary" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });

      if (!data || data.length === 0) return;

      const headers = data[0].filter((h) => h && h.trim() !== "");
      const rows = data.slice(1);

      const formattedData = rows.map((row) => {
        const record = {};
        headers.forEach((h, idx) => (record[h] = row[idx]));
        return record;
      });

      // Dynamic columns for uploaded file (temporary display before refresh)
      const cols = headers.map((h) => ({
        headerName: h,
        field: h,
        filter: true,
        sortable: true,
        resizable: true,
      }));

      setColumnDefs(cols);
      setRowData(formattedData);
      setOriginalRowData(formattedData);
      setTimeout(() => autoSizeAll(), 200);

      // 🚀 Save & Map, then refresh from backend
      if (dateValue && shiftValue) {
        await saveAndMap(formattedData);
        await refreshAttendanceGrid();   // 🔄 load backend-validated data
      } else {
        showToast("⚠️ Select Date & Shift before upload to map.", "orange");
      }
    };
    reader.readAsBinaryString(file);
  };

  const handleUpdateDepartment = async () => {
    if (!dateValue || !shiftValue) return showToast("⚠️ Select Date & Shift first!", "orange");
    try {
      const shiftName = getShiftNameById(shiftValue);
      if (!shiftName) return showToast("⚠️ Invalid shift!", "orange");

      const result = await api.getScnameEmptypeStatus({ atDate: dateValue, shiftName, type: "Department" });

      const table4 = result?.Result?.Table4 || []; // counts
      const table5 = result?.Result?.Table5 || []; // department names

      const mergedData = table4.map(d => {
        const deptNameObj = table5.find(t5 => t5.Department?.trim() === d.Department?.trim());
        return {
          EmpCode: d.EmpCode,
          EmpName: d.EmpName,
          Department: d.Department,
          TOTCNT: d.TOTCNT || 0,
          Department_Name: deptNameObj?.Department_Name || "",
          SelectedDept: deptNameObj?.Department_Name || d.Department || "" // ✅ bind current
        };
      });

      setDeptData(mergedData);
      setShowDeptModal(true);
    } catch (err) {
      console.error(err);
      showToast("❌ Failed to fetch department data!", "red");
    }
  };
const handleDeptSelect = (index, newDept) => {
  if (!newDept) return;

  // Find the department name from departments master
  const deptNameObj = deptData.find(d => d.Department === newDept) || {};
  const deptNameFromAPI = deptNameObj.Department_Name || newDept;

  setDeptData(prev =>
    prev.map((item, idx) =>
      idx === index
        ? { 
            ...item, 
            SelectedDept: newDept,
            CurrentDept: newDept,
            Department_Name: deptNameFromAPI // <-- update display name
          }
        : item
    )
  );
};

const handleUpdateDepartmentAttendanceApi = async () => {
  if (!deptData.length) 
    return showToast("⚠️ No department data to update!", "orange");

  // 🔹 Build correct payload
  const updates = deptData.map(d => ({
    Department_Name: d.Department_Name, // readable name from table
    Dept_Code: d.Department             // actual code
  }));

  try {
    const response = await api.UpdateDepartmentAttendance(updates);
    console.log("UpdateDepartmentAttendance response:", response);
    showToast("✅ Department Attendance Updated!", "green");
    setShowDeptModal(false);

    // 🔹 Update local grid (optional)
    setRowData(prev =>
      prev.map(emp => {
        const updated = updates.find(u => u.Dept_Code === emp.Department);
        return updated ? { ...emp, Department: updated.Department_Name } : emp;
      })
    );
  } catch (err) {
    console.error(err);
    showToast("❌ Failed to update department attendance!", "red");
  }
};

  // Auto-size columns
  const autoSizeAll = () => {
    const colApi = gridRef.current?.columnApi;
    if (!colApi) return;

    const fixedCols = ["Slno", "EmpCode", "SCName", "EmpType", "Hours"];
    const colsToAuto = colApi
      .getColumns()
      ?.map(c => c.getColId())
      .filter(id => !fixedCols.includes(id));

    if (colsToAuto?.length) {
      colApi.autoSizeColumns(colsToAuto, false);
    }
  };


  // Save & Vendor Mapping
  const saveAndMap = async (allRows) => {
    const selectedShift = shifts.find((s) => s.ShiftId.toString() === shiftValue.toString());
    const shiftName = selectedShift?.ShiftName || "";
    if (!shiftName) {
      showToast("⚠️ Invalid shift selected!", "orange");
      return;
    }

    const mappedRows = allRows.map((row) => ({
      ...row,
      Shift: shiftName,
      ShiftName: shiftName,
    }));

    try {
      const data = await api.checkVendorMapping({ date: dateValue, shift: shiftName, employees: mappedRows });
      if (data?.EmployeeData) applyApiResult(data.EmployeeData, "✅ File uploaded & vendor mapping done!");
    } catch (error) {
      console.error(error);
      showToast("❌ API call failed.", "red");
    }
  };

  // Save changes

  // Apply API result
  const applyApiResult = (resultData, toastMsg) => {
    if (!resultData?.Table) return;

    const mappedData = resultData.Table.map((row) => ({
      ...row,
      SeqID: row.SeqID,
      VendorID: row.VendorID,
      ItemID: row.ItemID,
    }));

    const allowedFields = ["Slno", "EmpCode", "EmpName", "SCName", "EmpType", "Hours", "MatchedSCName", "Department"];

    const newCols = allowedFields.map((field) => {
      if (field === "Slno") {
        return {
          headerName: "Slno",
          field: "Slno",
          width: 70,
          minWidth: 60,
          maxWidth: 80,
          suppressSizeToFit: true,
          filter: false,
          sortable: true,
          resizable: false,
        };
      }

      if (field === "EmpCode") {
        return {
          headerName: "EmpCode",
          field: "EmpCode",
          width: 100,
          minWidth: 120,
          maxWidth: 150,
          suppressSizeToFit: true,
          filter: true,
          sortable: true,
          resizable: false,
        };
      }

      if (field === "SCName") {
        return {
          headerName: "Subcontractor",
          field: "SCName",
          width: 120,
          minWidth: 120,
          maxWidth: 150,
          suppressSizeToFit: true,
          editable: true,
          resizable: false,
          cellEditor: "agSelectCellEditor",
          cellEditorParams: {
            values: vendors.map(v => v.vname),
          },
          valueFormatter: (params) => params.value,
        };
      }

      if (field === "EmpType") {
        return {
          headerName: "EmpType",
          field: "EmpType",
          width: 120,
          minWidth: 120,
          maxWidth: 150,
          editable: true,
          resizable: false,
          cellEditor: "agSelectCellEditor",
          cellEditorParams: {
            values: empTypeOptions.map(e => e.value),
          },
          valueFormatter: (params) => {
            const type = empTypeOptions.find(e => e.value.toString() === params.value?.toString());
            return type ? type.label : params.value;
          },
        };
      }

      if (field === "Hours") {
        return {
          headerName: "Hours",
          field: "Hours",
          width: 80,
          minWidth: 50,
          maxWidth: 70,
          filter: false,
          sortable: true,
          resizable: false,
        };
      }

      return { headerName: field, field, filter: true, sortable: true, resizable: true };
    });

    setColumnDefs(newCols);
    setRowData(mappedData);
    setOriginalRowData(mappedData);
    setTimeout(() => autoSizeAll(), 200);
    showToast(toastMsg, "green");
  };


  // Check attendance
  const handleCheckAttendance = async () => {
    if (!dateValue || !shiftValue) return alert("⚠️ Please select Date and Shift!");
    try {
      const response = await api.getAttendanceList(dateValue, shiftValue);
      const tables = response?.AttendanceList;
      setAttendanceCount(tables?.Table?.[0]?.AttendanceCount || 0);
      setAttendanceData(tables?.Table1 || []);
    } catch (error) {
      console.error(error);
      alert("Failed to fetch attendance.");
    }
  };
  const handlePushToDB = async () => {
    if (!rowData.length) return showToast("⚠️ No data to push!", "orange");
    if (!dateValue || !shiftValue) return showToast("⚠️ Select Date & Shift before pushing!", "orange");

    const selectedShift = shifts.find(s => s.ShiftId.toString() === shiftValue.toString());
    const shiftName = selectedShift?.ShiftName || "";
    const shiftId = selectedShift?.ShiftId || null;

    if (!shiftName || !shiftId) return showToast("⚠️ Invalid shift selected!", "orange");

    const username = sessionStorage.getItem("username") || "admin";
    const usrId = sessionStorage.getItem("userid") || "1";

    // ✅ Proper mapping for DB insert
    const attendanceItems = rowData.map((row, idx) => ({
      SeqID: row.Seqid || row.SeqID || idx + 1,
      ItemId: row.ItemId || null,
      ItemDesc: row.ItemDesc || "",
      Slno: row.Slno || idx + 1,
      EmpCode: row.EmpCode || "",
      EmpName: row.EmpName || "",
      SCName: row.SCName || "",
      EmpType: row.EmpType || "",
      Department: row.Department || "",
      Hours: parseFloat(row.Hours) || 0,
      AtDate: new Date(dateValue).toISOString(),
      ShiftName: shiftName,
      Vid: row.Vid || null,
      VName: row.MatchedSCName || row.VName || "",
    }));

    const payload = {
      Username: username,
      UsrId: usrId,
      ShiftId: shiftId,
      ShiftName: shiftName,
      AtDate: new Date(dateValue).toISOString(),
      AttendanceItems: attendanceItems,
    };

    try {
      const res = await api.insertOnlineAttendance(payload);
      if (res.Status === "Success") {
        showToast("✅ Attendance successfully pushed to DB!", "green");
        await refreshAttendanceGrid();
      } else if (res.Status === "Warning") {
        showToast(res.Message || "⚠️ Attendance already exists!", "orange");
      } else {
        showToast("❌ Unexpected server response!", "red");
      }
    } catch (err) {
      console.error("PushToDB Error:", err);

      const sqlMessage =
        err?.response?.data?.error ||
        err?.response?.data?.Message ||
        err?.message ||
        "❌ Failed to push attendance!";

      showToast(sqlMessage, "red");
    }
  };
  const handleUpdateSubContApi = async () => {
    if (!dateValue || !shiftValue) return alert("⚠️ Select Date & Shift!");

    const shiftObj = shifts.find(s => s.ShiftId.toString() === shiftValue.toString());
    const shiftName = shiftObj?.ShiftName || shiftValue;

    const updates = scData
      .filter(x => x.SelectedVendor || x.CurrentVendorId)
      .map(x => ({
        Vid: x.CurrentVendorId || null,
        Vname: x.CurrentVendor || "",
        SubCount: x.TOTCNT || 0,
        SubCode: x.SCName
      }));

    if (!updates.length) return showToast("⚠️ No data to update!", "orange");

    try {
      const data = await api.updateSubcontractorAttendance(updates);
      console.log("UpdateSubcontractorAttendance response:", data);
      showToast("✅ Subcontractor Attendance Updated!", "green");
      setShowSCModal(false);
      await refreshAttendanceGrid();
      setScData(prev =>
        prev.map(item => ({
          ...item,
          CurrentVendor: item.SelectedVendor,
          CurrentVendorId: item.CurrentVendorId
        }))
      );
    } catch (err) {
      console.error(err);
      showToast("❌ Failed to update Subcontractor Attendance!", "red");
    }
  };

  // Download Excel
  const handleDownloadExcel = () => {
    if (!attendanceData.length) return;
    const worksheet = XLSX.utils.json_to_sheet(attendanceData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Attendance");
    XLSX.writeFile(workbook, `Attendance_${dateValue}_${shiftValue}.xlsx`);
  };

  // Filter handling (unchanged)
  const handleFilterChange = async (key) => {
    const newFilters = { ...filters, [key]: !filters[key] };
    if (key.startsWith("sub")) newFilters.empMatched = newFilters.empUnmatched = false;
    if (key.startsWith("emp")) newFilters.subMatched = newFilters.subUnmatched = false;
    setFilters(newFilters);
    if (!Object.values(newFilters).some(Boolean)) return setRowData(originalRowData);

    const selectedShift = shifts.find(s => s.ShiftId.toString() === shiftValue.toString());
    const shiftName = selectedShift?.ShiftName || "";
    if (!shiftName) return showToast("⚠️ Invalid shift selected!", "orange");

    let combinedRows = [];
    if (newFilters.subMatched) {
      const res = await api.get("GetSubcontractorMatchStatus", { atDate: dateValue, shiftName, matchStatus: "Matched" });
      combinedRows = combinedRows.concat(res.Result?.Table || []);
    }
    if (newFilters.subUnmatched) {
      const res = await api.get("GetSubcontractorMatchStatus", { atDate: dateValue, shiftName, matchStatus: "Unmatched" });
      combinedRows = combinedRows.concat(res.Result?.Table || []);
    }
    if (newFilters.empMatched) {
      const res = await api.get("GetEmpTypeMatchStatus", { atDate: dateValue, shiftName, matchStatus: "Matched" });
      combinedRows = combinedRows.concat(res.Result?.Table || []);
    }
    if (newFilters.empUnmatched) {
      const res = await api.get("GetEmpTypeMatchStatus", { atDate: dateValue, shiftName, matchStatus: "Unmatched" });
      combinedRows = combinedRows.concat(res.Result?.Table || []);
    }

    setRowData(combinedRows);
  };

  // Toast utility
  const showToast = (msg, color) => {
    const toast = document.getElementById("toast");
    if (!toast) return;
    toast.innerText = msg;
    toast.style.background = color;
    toast.style.display = "block";
    setTimeout(() => (toast.style.display = "none"), 3000);
  };

  return (
    <div style={{ padding: "20px", fontFamily: "Segoe UI, sans-serif" }}>
      <LogoutButton />
      <h1 style={{ textAlign: "center", color: "#2C3E50" }}>
        Employee Attendance Data 📊
      </h1>

      {/* Date & Shift */}
      <div
        style={{
          margin: "15px 0",
          padding: "10px",
          background: "#f4f6f8",
          border: "1px solid #ddd",
          borderRadius: "6px",
          display: "flex",
          justifyContent: "center",
          gap: "20px",
          width: "80%",
          marginLeft: "auto",
          marginRight: "auto",
          fontSize: "16px",
          fontWeight: "500",
          color: "#2c3e50",
        }}
      >
        <div>
          📅 Date:{" "}
          <input
            type="date"
            value={dateValue}
            onChange={(e) => setDateValue(e.target.value)}
            style={{ padding: "5px" }}
          />
        </div>
        <div>
          🕒 Shift:{" "}
          <select
            value={shiftValue}
            onChange={(e) => setShiftValue(e.target.value)}
            style={{ padding: "5px" }}
          >
            <option value="">-- Select Shift --</option>
            {shifts.map((shift) => (
              <option key={shift.ShiftId} value={shift.ShiftId}>
                {shift.ShiftName}
              </option>
            ))}
          </select>
        </div>
        <button
          onClick={handleCheckAttendance}
          style={{
            padding: "8px 15px",
            borderRadius: "6px",
            border: "none",
            background: "#2980b9",
            color: "white",
            cursor: "pointer",
            fontWeight: "bold",
          }}
        >
          ✅ Check
        </button>
        {attendanceCount > 0 && (
          <button
            onClick={handleDownloadExcel}
            style={{
              padding: "8px 15px",
              borderRadius: "6px",
              border: "none",
              background: "#27ae60",
              color: "white",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            📥 Download
          </button>
        )}
      </div>

      {attendanceCount !== null && (
        <p
          style={{
            textAlign: "center",
            fontSize: "18px",
            marginTop: "10px",
          }}
        >
          Attendance Count: <b>{attendanceCount}</b>
        </p>
      )}

      {/* File Upload */}
      <div
        style={{
          margin: "20px auto",
          padding: "20px",
          border: "2px dashed #3498db",
          borderRadius: "10px",
          textAlign: "center",
          background: "#f9fbff",
          width: "60%",
        }}
      >
        <input
          type="file"
          accept=".csv,.xls,.xlsx"
          onChange={handleFileUpload}
          style={{ display: "none" }}
          id="fileUpload"
        />
        <label
          htmlFor="fileUpload"
          style={{
            cursor: "pointer",
            padding: "10px 20px",
            borderRadius: "5px",
            background: "#3498db",
            color: "#fff",
            fontWeight: "bold",
          }}
        >
          📂 Upload CSV/Excel (Auto Save & Map)
        </label>
        {fileName && (
          <p style={{ marginTop: "10px", color: "#34495e" }}>
            📑 Loaded File: <b>{fileName}</b> ({rowData.length} records)
          </p>
        )}
      </div>

      {/* Filters */}
      <div
        style={{
          margin: "10px auto",
          padding: "10px",
          background: "#eef3f9",
          border: "1px solid #ccc",
          borderRadius: "6px",
          width: "95%",
          display: "flex",
          gap: "20px",
          justifyContent: "center",
        }}
      >
        <label>
          <input
            type="checkbox"
            checked={filters.subMatched}
            onChange={() => handleFilterChange("subMatched")}
            disabled={filters.empMatched || filters.empUnmatched}
          />{" "}
          Subcontractor Matched
        </label>
        <label>
          <input
            type="checkbox"
            checked={filters.subUnmatched}
            onChange={() => handleFilterChange("subUnmatched")}
            disabled={filters.empMatched || filters.empUnmatched}
          />{" "}
          Subcontractor Unmatched
        </label>
        <label>
          <input
            type="checkbox"
            checked={filters.empMatched}
            onChange={() => handleFilterChange("empMatched")}
            disabled={filters.subMatched || filters.subUnmatched}
          />{" "}
          EmpType Matched
        </label>
        <label>
          <input
            type="checkbox"
            checked={filters.empUnmatched}
            onChange={() => handleFilterChange("empUnmatched")}
            disabled={filters.subMatched || filters.subUnmatched}
          />{" "}
          EmpType Unmatched
        </label>
      </div>

      {/* Action buttons */}
      <div style={{ textAlign: "center", marginTop: "20px", display: "flex", justifyContent: "center", gap: "15px" }}>
        <button
          onClick={handleUpdateSC}
          style={{
            backgroundColor: "#4CAF50",   // green
            color: "white",
            padding: "8px 16px",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "14px"
          }}
        >
          🔄 Update Subcontractor
        </button>

        <button
          onClick={handleUpdateEmpType}
          style={{
            backgroundColor: "#2196F3",   // blue
            color: "white",
            padding: "8px 16px",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "14px"
          }}
        >
          🔄 Update EmpType
        </button>

        <button
          onClick={handleUpdateDepartment}
          style={{
            backgroundColor: "#9b59b6", // purple
            color: "white",
            padding: "8px 16px",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "14px"
          }}
        >
          🔄 Update Department
        </button>
      </div>

      {showSCModal && (
        <div className="modal-overlay" style={overlayStyle}>
          <div className="modal" style={modalStyle}>
            <button className="modal-close" onClick={() => setShowSCModal(false)}>×</button>
            <h3>Update Subcontractor</h3>
            <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "10px" }}>
              <thead>
                <tr style={{ background: "#f4f4f4" }}>
                  <th style={thStyle}>SC Name</th>
                  <th style={thStyle}>Count</th>
                  <th style={thStyle}>Vendor</th>
                  <th style={thStyle}>Vid</th>
                  <th style={thStyle}>Select Vendor</th>
                </tr>
              </thead>
              <tbody>
                {scData.map((item, idx) => (
                  <tr key={idx}>
                    <td style={tdStyle}>{item.SCName}</td>
                    <td style={tdStyle}>{item.TOTCNT}</td>
                    <td style={tdStyle}>{item.CurrentVendor}</td>
                    <td style={tdStyle}>{item.CurrentVendorId || "-"}</td>
                    <td style={tdStyle}>
                      <select
                        value={item.CurrentVendorId || ""}  // use vendor ID here
                        onChange={(e) => handleSCSelect(item.SCName, e.target.value)}
                      >
                        <option value="">-- Select Vendor --</option>
                        {vendors.map(v => (
                          <option key={v.vid} value={v.vid}>{v.vname}</option>
                        ))}
                      </select>
                    </td>

                  </tr>
                ))}
              </tbody>
            </table>

            {/* ✅ Update Button */}
            <div style={{ marginTop: "15px", textAlign: "right" }}>
              <button
                onClick={handleUpdateSubContApi}
                style={{
                  padding: "8px 20px",
                  borderRadius: "5px",
                  border: "none",
                  background: "#2980b9",
                  color: "#fff",
                  fontWeight: "bold",
                  cursor: "pointer",
                }}
              >
                Update Subcontractor
              </button>
            </div>
          </div>
        </div>
      )}

      {/* EmpType Modal */}
      {showEmpModal && (
        <div className="modal-overlay">
          <div className="modal">
            <button
              className="modal-close"
              onClick={() => setShowEmpModal(false)}
            >
              ×
            </button>
            <h3>Update EmpType</h3>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                marginTop: "10px",
              }}
            >
              <thead>
                <tr style={{ background: "#f4f4f4" }}>
                  <th style={{ border: "1px solid #ddd", padding: "8px" }}>
                    EmpType
                  </th>
                  <th style={{ border: "1px solid #ddd", padding: "8px" }}>
                    Count
                  </th>
                  <th style={{ border: "1px solid #ddd", padding: "8px" }}>
                    EmpCode
                  </th>
                  <th style={{ border: "1px solid #ddd", padding: "8px" }}>
                    ItemId
                  </th>
                  <th style={{ border: "1px solid #ddd", padding: "8px" }}>
                    Select EmpType
                  </th>
                </tr>
              </thead>
              <tbody>
                {empTypeData.map((item, idx) => (
                  <tr key={idx}>
                    <td style={tdStyle}>{item.EmpTypeLabel}</td>
                    <td style={tdStyle}>{item.TOTCNT}</td>
                    <td style={tdStyle}>{item.CurrentEmpType}</td>
                    <td style={tdStyle}>{item.CurrentEmpTypeId}</td>
                    <td style={tdStyle}>
                      <select
                        value={item.SelectedEmpType || ""}
                        onChange={(e) => {
                          const selected = empTypeOptions.find(opt => opt.value === e.target.value);
                          handleEmpTypeSelect(idx, selected?.value, selected?.itemId); // use index
                        }}
                      >
                        <option value="">-- Select EmpType --</option>
                        {empTypeOptions.map(opt => (
                          <option key={opt.itemId} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>

                    </td>

                  </tr>
                ))}
              </tbody>
            </table>

            {/* ✅ Update Button */}
            <div style={{ marginTop: "15px", textAlign: "right" }}>
              <button
                onClick={handleUpdateEmpTypeApi}
                style={{
                  padding: "8px 20px",
                  borderRadius: "5px",
                  border: "none",
                  background: "#27ae60",
                  color: "#fff",
                  fontWeight: "bold",
                  cursor: "pointer",
                }}
              >
                Update EmpType
              </button>
            </div>
          </div>
        </div>
      )}


      {showDeptModal && (
        <div className="modal-overlay" style={overlayStyle}>
          <div className="modal" style={modalStyle}>
            <button className="modal-close" onClick={() => setShowDeptModal(false)}>×</button>
            <h3>Update Department</h3>
            <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "10px" }}>
              <thead>
                <tr style={{ background: "#f4f4f4" }}>
                  <th style={thStyle}>Department</th>
                  <th style={thStyle}>Count</th>
                  <th style={thStyle}>Department Name</th>
                  <th style={thStyle}>Select Dept</th>
                </tr>
              </thead>
              <tbody>
                {deptData.map((item, idx) => (
                  <tr key={idx}>
                    <td style={tdStyle}>{item.Department}</td>
                    <td style={tdStyle}>{item.TOTCNT}</td>
                    <td style={tdStyle}>{item.Department_Name || "-"}</td>
                    <td style={tdStyle}>
                      <select
                        value={item.SelectedDept || ""}
                        onChange={(e) => handleDeptSelect(idx, e.target.value)}
                      >
                        <option value="">-- Select Dept --</option>
                        {departments.map((d, i) => (
                          <option key={i} value={d}>{d}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ marginTop: "15px", textAlign: "right" }}>
              <button
                onClick={handleUpdateDepartmentAttendanceApi}
                style={{
                  padding: "8px 20px",
                  borderRadius: "5px",
                  border: "none",
                  background: "#9b59b6",
                  color: "#fff",
                  fontWeight: "bold",
                  cursor: "pointer",
                }}
              >
                Update Department
              </button>
            </div>
          </div>
        </div>
      )}
      {/* AG Grid */}
      <div style={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
        <div
          className="ag-theme-quartz custom-grid"
          style={{ height: 500, width: "auto", minWidth: "85%" }}
        >
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            columnDefs={columnDefs}
            pagination={false}
            animateRows={true}
            defaultColDef={{ resizable: true, filter: true, sortable: true }}
            onGridReady={() => setTimeout(() => autoSizeAll(), 200)}
            onCellValueChanged={async (params) => {
              const { data, colDef, newValue, oldValue } = params;
              if (newValue === oldValue || !data.SeqID) return;

              if (colDef.field === "SCName") {
                const vendor = vendors.find((v) => v.vname === newValue);
                data.VendorID = vendor?.vid || null;
                data.VendorName = vendor?.vname || null;
              }

              if (colDef.field === "EmpType") {
                data.EmpType = newValue;
              }

              try {
                await api.post("UpdateOnlineAttendance", {
                  SeqID: data.SeqID,
                  VendorID: data.VendorID,
                  VendorName: data.VendorName,
                  ItemID: data.ItemID,
                  EmpType: data.EmpType,
                });

                showToast(`✅ ${colDef.headerName} updated!`, "green");
                setRowData([...rowData]); // refresh grid
              } catch (err) {
                console.error(err);
                showToast(`❌ Failed to update ${colDef.headerName}!`, "red");
              }
            }}
          />
        </div>
      </div>

      {/* Push to DB Button */}
      <div style={{ textAlign: "center", marginTop: "20px" }}>
        <button
          onClick={handlePushToDB}
          style={{
            padding: "10px 25px",
            borderRadius: "6px",
            border: "none",
            background: "#e67e22",
            color: "white",
            fontWeight: "bold",
            cursor: "pointer",
          }}
        >
          🚀 Push to DB
        </button>
      </div>

      {/* Toast */}
      <div
        id="toast"
        style={{
          display: "none",
          position: "fixed",
          bottom: "20px",
          right: "20px",
          background: "green",
          color: "white",
          padding: "12px 20px",
          borderRadius: "8px",
          boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.2)",
          fontWeight: "500",
        }}
      ></div>
    </div>
  );
};
export default EmployeeDataGrid;
